import { createGoogleReviewClient } from './google-client'
import { createFetchReviewTransport } from './transport'
import type { GooglePlaceSummary, GoogleReview } from './types'

export interface GoogleReviewWidgetOptions {
  baseUrl?: string
  placeId: string
  title?: string
  reviewUrl?: string
  reviewButtonText?: string
  showReviewButton?: boolean
  maxReviews?: number
  accessToken?: string
  onUnauthorized?: () => void
  className?: string
}

export interface GoogleReviewWidgetHandle {
  unmount(): void
  refresh(): Promise<void>
}

export interface GoogleReviewWidgetGlobalApi {
  mount(
    target: string | Element | ArrayLike<Element>,
    options: GoogleReviewWidgetOptions
  ): GoogleReviewWidgetHandle[]
}

declare global {
  interface Window {
    GoogleReviewWidget?: GoogleReviewWidgetGlobalApi
    mountGoogleReviewWidget?: typeof mountGoogleReviewWidget
  }
}

declare const __REVIEW_WIDGET_DEFAULT_BASE_URL__: string

const GOOGLE_STYLE_ID = 'grw-widget-styles'

const DEFAULT_BASE_URL =
  typeof __REVIEW_WIDGET_DEFAULT_BASE_URL__ === 'string'
    ? __REVIEW_WIDGET_DEFAULT_BASE_URL__.trim().replace(/\/$/, '')
    : ''

function resolveBaseUrl(baseUrl?: string) {
  const resolved = (baseUrl || DEFAULT_BASE_URL).trim().replace(/\/$/, '')
  if (!resolved) {
    throw new Error(
      'Google review widget base URL is not configured. Pass `baseUrl` to mountGoogleReviewWidget(...).'
    )
  }
  return resolved
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function formatDate(value?: number) {
  if (!value) return ''
  const date = new Date(value * 1000)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric'
  }).format(date)
}

function truncateText(value: string, maxLength = 180) {
  const trimmed = value.trim()
  if (trimmed.length <= maxLength) return trimmed
  return `${trimmed.slice(0, maxLength).trimEnd()}...`
}

function formatReplyDate(value?: number) {
  if (!value) return ''
  const date = new Date(value * 1000)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric'
  }).format(date)
}

function googleStarMarkup(rating: number, size = 16) {
  return Array.from({ length: 5 }, (_, i) => {
    const filled = i + 1 <= Math.round(rating)
    return `<span class="grw-star ${filled ? 'grw-star--on' : 'grw-star--off'}">
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
      </svg>
    </span>`
  }).join('')
}

const GOOGLE_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
</svg>`

function defaultReviewUrl(placeId: string) {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`
}

function ensureGoogleStyles() {
  if (typeof document === 'undefined') return
  if (document.getElementById(GOOGLE_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = GOOGLE_STYLE_ID
  style.textContent = `
    .grw-widget {
      font-family: Inter, Arial, sans-serif;
      color: #111827;
      --grw-star: #f7b500;
      --grw-primary: #1d7cf2;
      --grw-primary-hover: #1667d6;
      --grw-text: #111827;
      --grw-muted: #6b7280;
      --grw-border: rgba(17, 24, 39, 0.08);
      --grw-bg: #ffffff;
      --grw-surface: #ffffff;
      --grw-shadow: 0 20px 50px rgba(17, 24, 39, 0.06);
      --grw-radius: 20px;
    }
    .grw-widget,
    .grw-widget *,
    .grw-widget *::before,
    .grw-widget *::after {
      box-sizing: border-box;
    }
    .grw-card {
      background: var(--grw-bg);
      border: 1px solid var(--grw-border);
      border-radius: var(--grw-radius);
      box-shadow: var(--grw-shadow);
      overflow: hidden;
    }
    .grw-shell {
      padding: 28px 28px 24px;
    }
    .grw-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 34px;
      flex-wrap: wrap;
    }
    .grw-brand {
      display: grid;
      gap: 8px;
      min-width: 0;
    }
    .grw-title {
      margin: 0;
      font-size: 20px;
      font-weight: 800;
      line-height: 1.1;
      letter-spacing: -0.03em;
    }
    .grw-title__google {
      color: #4285f4;
      font-weight: 800;
    }
    .grw-title__reviews {
      color: #111827;
      font-weight: 700;
    }
    .grw-summary-row {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .grw-score {
      font-size: 26px;
      font-weight: 800;
      letter-spacing: -0.04em;
      line-height: 1;
    }
    .grw-stars {
      display: inline-flex;
      gap: 2px;
      line-height: 0;
    }
    .grw-star {
      display: inline-flex;
      line-height: 0;
    }
    .grw-star--on {
      color: var(--grw-star);
    }
    .grw-star--off {
      color: #e5e7eb;
    }
    .grw-count {
      color: var(--grw-muted);
      font-size: 13px;
    }
    .grw-cta {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border: 0;
      text-decoration: none;
      padding: 12px 20px;
      border-radius: 999px;
      background: var(--grw-primary);
      color: #fff;
      font-size: 14px;
      font-weight: 700;
      box-shadow: 0 14px 30px rgba(29, 124, 242, 0.18);
      transition: transform 160ms ease, background 160ms ease, box-shadow 160ms ease;
      white-space: nowrap;
    }
    .grw-cta:hover {
      background: var(--grw-primary-hover);
      transform: translateY(-1px);
      box-shadow: 0 16px 34px rgba(29, 124, 242, 0.24);
    }
    .grw-carousel-shell {
      position: relative;
    }
    .grw-carousel {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(220px, 1fr);
      gap: 22px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      padding: 8px 30px 12px;
      margin: 0 -10px;
      scrollbar-width: none;
    }
    .grw-carousel::-webkit-scrollbar {
      display: none;
    }
    .grw-card-item {
      scroll-snap-align: start;
      display: grid;
      gap: 12px;
      min-width: 0;
    }
    .grw-review-head {
      display: grid;
      grid-template-columns: 44px 1fr auto;
      gap: 10px;
      align-items: center;
      min-width: 0;
    }
    .grw-avatar {
      width: 44px;
      height: 44px;
      border-radius: 999px;
      overflow: hidden;
      background: linear-gradient(135deg, #dbeafe, #bfdbfe);
      display: grid;
      place-items: center;
      font-size: 16px;
      font-weight: 800;
      color: #1f2937;
      flex: 0 0 auto;
    }
    .grw-avatar--sm {
      width: 32px;
      height: 32px;
      font-size: 13px;
    }
    .grw-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
    }
    .grw-reviewer {
      min-width: 0;
    }
    .grw-reviewer__name-row {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .grw-reviewer__name {
      font-weight: 700;
      font-size: 14px;
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .grw-verification {
      width: 14px;
      height: 14px;
      flex: 0 0 auto;
      color: #1d7cf2;
    }
    .grw-review-date {
      color: var(--grw-muted);
      font-size: 12px;
      margin-top: 3px;
    }
    .grw-review-stars {
      justify-self: end;
      display: flex;
      gap: 2px;
      line-height: 0;
    }
    .grw-review-rating {
      color: var(--grw-star);
    }
    .grw-review-text {
      margin: 0;
      font-size: 14px;
      line-height: 1.55;
      color: var(--grw-text);
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      min-height: 88px;
    }
    .grw-read-more {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: var(--grw-primary);
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      border: 0;
      background: transparent;
      padding: 0;
      cursor: pointer;
      font: inherit;
      margin-top: -2px;
    }
    .grw-read-more:hover {
      text-decoration: underline;
    }
    .grw-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(17, 24, 39, 0.68);
      display: grid;
      place-items: center;
      padding: 20px;
      z-index: 10000;
    }
    .grw-modal {
      width: min(760px, 100%);
      max-height: calc(100vh - 40px);
      overflow: auto;
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 30px 80px rgba(17, 24, 39, 0.32);
      padding: 28px 28px 24px;
      border: 1px solid rgba(17, 24, 39, 0.08);
      position: relative;
    }
    .grw-modal__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 20px;
    }
    .grw-modal__title-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      min-width: 0;
    }
    .grw-modal__title-stack {
      display: grid;
      gap: 4px;
      min-width: 0;
    }
    .grw-modal__title {
      margin: 0;
      font-size: 18px;
      font-weight: 800;
      line-height: 1.2;
      color: #111827;
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .grw-modal__title-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .grw-modal__verified {
      width: 16px;
      height: 16px;
      color: #1d7cf2;
      flex: 0 0 auto;
    }
    .grw-modal__subtle {
      font-size: 13px;
      color: var(--grw-muted);
    }
    .grw-modal__close {
      position: absolute;
      top: 14px;
      right: 14px;
      width: 34px;
      height: 34px;
      border: 0;
      background: rgba(17, 24, 39, 0.04);
      color: #6b7280;
      border-radius: 999px;
      display: grid;
      place-items: center;
      cursor: pointer;
      padding: 0;
      transition: background 160ms ease, color 160ms ease, transform 160ms ease;
    }
    .grw-modal__close:hover {
      background: rgba(17, 24, 39, 0.08);
      color: #111827;
      transform: scale(1.03);
    }
    .grw-modal__close svg {
      display: block;
    }
    .grw-modal__body {
      display: grid;
      gap: 18px;
    }
    .grw-modal__text {
      margin: 0;
      font-size: 15px;
      line-height: 1.72;
      color: #111827;
      white-space: pre-wrap;
    }
    .grw-modal__stars {
      display: inline-flex;
      gap: 2px;
      line-height: 0;
    }
    .grw-modal__reply {
      display: grid;
      gap: 10px;
      padding: 12px 0 0 14px;
      border-left: 2px solid rgba(17, 24, 39, 0.12);
      margin-top: 4px;
    }
    .grw-modal__reply-head {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      min-width: 0;
    }
    .grw-modal__reply-head-body {
      display: grid;
      gap: 4px;
      min-width: 0;
    }
    .grw-modal__reply-name {
      font-size: 14px;
      font-weight: 800;
      color: #111827;
    }
    .grw-modal__reply-name-row {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      flex-wrap: wrap;
    }
    .grw-modal__reply-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 6px;
      border-radius: 999px;
      background: #eef2ff;
      color: #1f2937;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      white-space: nowrap;
    }
    .grw-modal__reply-meta {
      font-size: 12px;
      color: var(--grw-muted);
    }
    .grw-modal__reply-text {
      margin: 0;
      font-size: 14px;
      line-height: 1.68;
      color: #111827;
      white-space: pre-wrap;
    }
    .grw-nav {
      position: absolute;
      top: 48%;
      transform: translateY(-50%);
      width: 38px;
      height: 38px;
      border-radius: 999px;
      border: 0;
      background: rgba(17, 24, 39, 0.46);
      color: #fff;
      display: grid;
      place-items: center;
      cursor: pointer;
      z-index: 2;
      transition: transform 160ms ease, background 160ms ease, opacity 160ms ease;
      backdrop-filter: blur(6px);
    }
    .grw-nav:hover {
      background: rgba(17, 24, 39, 0.62);
      transform: translateY(-50%) scale(1.03);
    }
    .grw-nav:disabled {
      opacity: 0.35;
      cursor: default;
    }
    .grw-nav--prev {
      left: 0;
    }
    .grw-nav--next {
      right: 0;
    }
    .grw-nav svg {
      width: 18px;
      height: 18px;
    }
    .grw-dots {
      display: flex;
      justify-content: center;
      gap: 6px;
      margin-top: 14px;
    }
    .grw-dot {
      width: 6px;
      height: 6px;
      border-radius: 999px;
      border: 0;
      background: #d1d5db;
      padding: 0;
      cursor: pointer;
      transition: transform 160ms ease, background 160ms ease, width 160ms ease;
    }
    .grw-dot--active {
      width: 18px;
      background: #111827;
    }
    .grw-loading {
      padding: 24px;
      text-align: center;
      color: var(--grw-muted);
      font-size: 14px;
    }
    .grw-error {
      padding: 14px 16px;
      border-radius: 14px;
      background: #fef2f2;
      color: #dc2626;
      font-size: 14px;
      border: 1px solid #fecaca;
    }
    .grw-empty {
      padding: 20px;
      text-align: center;
      color: var(--grw-muted);
      font-size: 14px;
    }
    .grw-footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 8px;
    }
    .grw-powered {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: var(--grw-muted);
      font-size: 12px;
    }
    @media (max-width: 920px) {
      .grw-carousel {
        grid-auto-columns: minmax(220px, 74vw);
      }
      .grw-shell {
        padding: 22px 18px 18px;
      }
    }
    @media (max-width: 640px) {
      .grw-header {
        margin-bottom: 26px;
      }
      .grw-carousel {
        grid-auto-columns: minmax(220px, 82vw);
        padding-inline: 22px;
      }
      .grw-nav {
        top: 42%;
      }
    }
  `
  document.head.appendChild(style)
}

function renderAvatar(review: GoogleReview) {
  if (review.profile_photo_url) {
    return `<div class="grw-avatar"><img src="${escapeHtml(review.profile_photo_url)}" alt="${escapeHtml(review.author_name)}" loading="lazy" /></div>`
  }
  const initial = (review.author_name.trim().charAt(0) || 'G').toUpperCase()
  return `<div class="grw-avatar">${escapeHtml(initial)}</div>`
}

function renderReplyAvatar(reply: NonNullable<GoogleReview['reply']>) {
  if (reply.profile_photo_url) {
    return `<div class="grw-avatar grw-avatar--sm"><img src="${escapeHtml(reply.profile_photo_url)}" alt="${escapeHtml(reply.author_name || 'Reply author')}" loading="lazy" /></div>`
  }
  const initial = (reply.author_name?.trim().charAt(0) || 'O').toUpperCase()
  return `<div class="grw-avatar grw-avatar--sm">${escapeHtml(initial)}</div>`
}

function renderReviewCard(review: GoogleReview, index: number) {
  return `
    <article class="grw-card-item">
      <div class="grw-review-head">
        ${renderAvatar(review)}
        <div class="grw-reviewer">
          <div class="grw-reviewer__name-row">
            <div class="grw-reviewer__name">${escapeHtml(review.author_name)}</div>
            <svg class="grw-verification" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2l2.2 2.7 3.4-.1 1.3 3.1 3.1 1.3-.1 3.4L24 15l-2.7 2.2.1 3.4-3.1 1.3-1.3 3.1-3.4-.1L12 24l-2.2-2.7-3.4.1-1.3-3.1-3.1-1.3.1-3.4L0 15l2.7-2.2-.1-3.4 3.1-1.3 1.3-3.1 3.4.1L12 2zm-1.2 13.3l6-6-1.4-1.4-4.6 4.6-2.2-2.2-1.4 1.4 3.6 3.6z"/>
            </svg>
          </div>
          <div class="grw-review-date">${escapeHtml(review.relative_time_description || formatDate(review.time))}</div>
        </div>
        <div class="grw-review-stars" aria-label="Rated ${review.rating} out of 5 stars">
          ${googleStarMarkup(review.rating, 16)}
        </div>
      </div>
      <p class="grw-review-text">${escapeHtml(truncateText(review.text || '', 180) || ' ')}</p>
      ${review.text ? `<button type="button" class="grw-read-more" data-action="open-google-review-modal" data-review-index="${index}">Read more</button>` : ''}
    </article>
  `
}

function renderReplyBlock(reply: NonNullable<GoogleReview['reply']>) {
  const replyName = reply.author_name?.trim() || 'Owner Reply'
  return `
    <div class="grw-modal__reply">
      <div class="grw-modal__reply-head">
        ${renderReplyAvatar(reply)}
        <div class="grw-modal__reply-head-body">
          <div class="grw-modal__reply-name-row">
            <div class="grw-modal__reply-name">${escapeHtml(replyName)}</div>
            <span class="grw-modal__reply-badge">Owner Reply</span>
          </div>
          <div class="grw-modal__reply-meta">${escapeHtml(reply.relative_time_description || formatReplyDate(reply.time))}</div>
        </div>
      </div>
      <p class="grw-modal__reply-text">${escapeHtml(reply.text || '')}</p>
    </div>
  `
}

function renderHeader(data: GooglePlaceSummary, reviewLink: string, showButton: boolean, buttonText: string) {
  return `
    <div class="grw-header">
      <div class="grw-brand">
        <h3 class="grw-title"><span class="grw-title__google">Google</span> <span class="grw-title__reviews">Reviews</span></h3>
        <div class="grw-summary-row">
          <div class="grw-score">${Number(data.rating).toFixed(1)}</div>
          <div class="grw-stars" aria-label="Average rating ${data.rating} out of 5">
            ${googleStarMarkup(data.rating, 20)}
          </div>
          <div class="grw-count">(${Number(data.total_ratings).toLocaleString()})</div>
        </div>
      </div>
      ${showButton ? `<a class="grw-cta" href="${escapeHtml(reviewLink)}" target="_blank" rel="noopener noreferrer">${escapeHtml(buttonText)}</a>` : ''}
    </div>
  `
}

function renderEmptyState() {
  return `<div class="grw-empty">No Google reviews available.</div>`
}

function renderErrorState(message: string) {
  return `<div class="grw-error">${escapeHtml(message)}</div>`
}

class GoogleReviewWidgetInstance implements GoogleReviewWidgetHandle {
  private readonly root: HTMLElement
  private destroyed = false
  private data: GooglePlaceSummary | null = null
  private loading = true
  private error: string | null = null
  private carouselEl: HTMLElement | null = null
  private scrollRaf = 0
  private activePage = 0
  private openedReviewIndex: number | null = null

  constructor(private readonly options: GoogleReviewWidgetOptions, root: HTMLElement) {
    ensureGoogleStyles()
    this.root = root
    this.root.classList.add('grw-widget')
    if (options.className) {
      for (const cls of options.className.split(/\s+/).filter(Boolean)) {
        this.root.classList.add(cls)
      }
    }
    this.render()
    void this.load()
  }

  private getReviewUrl() {
    return this.options.reviewUrl?.trim() || defaultReviewUrl(this.options.placeId)
  }

  private getReviewButtonText() {
    return this.options.reviewButtonText?.trim() || 'Review us on Google'
  }

  private getVisibleReviews() {
    if (!this.data) return []
    return this.options.maxReviews ? this.data.reviews.slice(0, this.options.maxReviews) : this.data.reviews
  }

  private getPageCount() {
    if (!this.carouselEl) return 1
    const width = this.carouselEl.clientWidth || 1
    const total = this.carouselEl.scrollWidth || 1
    return Math.max(1, Math.ceil(total / width))
  }

  private syncCarouselState() {
    if (!this.carouselEl || this.destroyed) return
    const maxScroll = Math.max(this.carouselEl.scrollWidth - this.carouselEl.clientWidth, 1)
    const ratio = this.carouselEl.scrollLeft / maxScroll
    const pageCount = this.getPageCount()
    this.activePage = Math.min(pageCount - 1, Math.max(0, Math.round(ratio * (pageCount - 1))))
    this.updateNavState()
    this.updateDotsState()
  }

  private updateNavState() {
    const prev = this.root.querySelector<HTMLButtonElement>('[data-action="carousel-prev"]')
    const next = this.root.querySelector<HTMLButtonElement>('[data-action="carousel-next"]')
    if (!this.carouselEl || !prev || !next) return
    const nearStart = this.carouselEl.scrollLeft <= 4
    const nearEnd = this.carouselEl.scrollLeft + this.carouselEl.clientWidth >= this.carouselEl.scrollWidth - 4
    prev.disabled = nearStart
    next.disabled = nearEnd
  }

  private updateDotsState() {
    const dots = this.root.querySelectorAll<HTMLButtonElement>('[data-dot-index]')
    dots.forEach((dot, index) => {
      dot.classList.toggle('grw-dot--active', index === this.activePage)
      dot.setAttribute('aria-pressed', String(index === this.activePage))
    })
  }

  private scrollByPages(direction: number) {
    if (!this.carouselEl) return
    const amount = Math.max(this.carouselEl.clientWidth * 0.85, 260)
    this.carouselEl.scrollBy({ left: amount * direction, behavior: 'smooth' })
  }

  private scrollToPage(index: number) {
    if (!this.carouselEl) return
    const pageCount = this.getPageCount()
    const targetIndex = Math.max(0, Math.min(index, pageCount - 1))
    const targetLeft = (this.carouselEl.scrollWidth / pageCount) * targetIndex
    this.carouselEl.scrollTo({ left: targetLeft, behavior: 'smooth' })
  }

  private handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    const actionEl = target?.closest<HTMLElement>('[data-action]')
    if (!actionEl || !this.root.contains(actionEl)) return

    const action = actionEl.dataset.action
    if (action === 'open-google-review-modal') {
      const index = Number(actionEl.dataset.reviewIndex || '')
      if (Number.isFinite(index)) {
        this.openedReviewIndex = index
        this.render()
      }
      return
    }
    if (action === 'close-google-review-modal') {
      this.openedReviewIndex = null
      this.render()
      return
    }
    if (action === 'carousel-prev') {
      this.scrollByPages(-1)
      return
    }
    if (action === 'carousel-next') {
      this.scrollByPages(1)
      return
    }
    if (action === 'carousel-dot') {
      const index = Number(actionEl.dataset.dotIndex || '0')
      if (Number.isFinite(index)) {
        this.scrollToPage(index)
      }
    }
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.openedReviewIndex != null) {
      this.openedReviewIndex = null
      this.render()
    }
  }

  private handleScroll = () => {
    if (this.scrollRaf) return
    this.scrollRaf = window.requestAnimationFrame(() => {
      this.scrollRaf = 0
      this.syncCarouselState()
    })
  }

  private async load() {
    this.loading = true
    this.error = null
    this.openedReviewIndex = null
    this.render()

    try {
      const resolveAccessToken = () => this.options.accessToken ?? null

      const transport = createFetchReviewTransport({
        baseUrl: resolveBaseUrl(this.options.baseUrl),
        getAccessToken: resolveAccessToken,
        ...(this.options.onUnauthorized ? { onUnauthorized: this.options.onUnauthorized } : {})
      })

      const client = createGoogleReviewClient({ transport })
      const response = await client.fetchGoogleReviews(this.options.placeId)

      if (response.result) {
        this.data = response.result
      } else if (response && typeof response === 'object' && 'name' in response) {
        this.data = response as unknown as GooglePlaceSummary
      } else {
        this.data = null
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load Google reviews'
    } finally {
      this.loading = false
      this.render()
    }
  }

  private render() {
    if (this.destroyed) return

    const reviewUrl = this.getReviewUrl()
    const visibleReviews = this.getVisibleReviews()
    const showButton = this.options.showReviewButton !== false
    const openedReview =
      this.openedReviewIndex != null ? visibleReviews[this.openedReviewIndex] ?? null : null

    let bodyHtml: string

    if (this.loading) {
      bodyHtml = `<div class="grw-loading">Loading Google reviews...</div>`
    } else if (this.error) {
      bodyHtml = renderErrorState(this.error)
    } else if (!this.data) {
      bodyHtml = renderEmptyState()
    } else {
      bodyHtml = `
        ${renderHeader(this.data, reviewUrl, showButton, this.getReviewButtonText())}
        <div class="grw-carousel-shell">
          <button type="button" class="grw-nav grw-nav--prev" data-action="carousel-prev" aria-label="Previous reviews">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
          <button type="button" class="grw-nav grw-nav--next" data-action="carousel-next" aria-label="Next reviews">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
          <div class="grw-carousel" data-carousel>
            ${visibleReviews.length
              ? visibleReviews.map((review, index) => renderReviewCard(review, index)).join('')
              : `<div class="grw-empty">No reviews yet.</div>`}
          </div>
          <div class="grw-dots" aria-label="Review pagination">
            ${Array.from({ length: Math.min(5, Math.max(1, Math.ceil(visibleReviews.length / 2))) }, (_, index) =>
              `<button type="button" class="grw-dot ${index === this.activePage ? 'grw-dot--active' : ''}" data-action="carousel-dot" data-dot-index="${index}" aria-label="Go to review page ${index + 1}" aria-pressed="${index === this.activePage}"></button>`
            ).join('')}
          </div>
        </div>
        <div class="grw-footer">
          <span class="grw-powered">${GOOGLE_LOGO_SVG}<span>Powered by Google</span></span>
        </div>
      `
    }

    const modalHtml = openedReview
      ? `
        <div class="grw-modal-overlay" data-action="close-google-review-modal">
          <div class="grw-modal" data-action="keep-open" role="dialog" aria-modal="true" aria-label="Google review details">
            <div class="grw-modal__head">
              <div class="grw-modal__title-row">
                ${renderAvatar(openedReview)}
                <div class="grw-modal__title-stack">
                  <h3 class="grw-modal__title">
                    <span class="grw-modal__title-name">${escapeHtml(openedReview.author_name)}</span>
                    <svg class="grw-modal__verified" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2l2.2 2.7 3.4-.1 1.3 3.1 3.1 1.3-.1 3.4L24 15l-2.7 2.2.1 3.4-3.1 1.3-1.3 3.1-3.4-.1L12 24l-2.2-2.7-3.4.1-1.3-3.1-3.1-1.3.1-3.4L0 15l2.7-2.2-.1-3.4 3.1-1.3 1.3-3.1 3.4.1L12 2zm-1.2 13.3l6-6-1.4-1.4-4.6 4.6-2.2-2.2-1.4 1.4 3.6 3.6z"/>
                    </svg>
                  </h3>
                  <div class="grw-modal__subtle">${escapeHtml(openedReview.relative_time_description || formatDate(openedReview.time))}</div>
                </div>
              </div>
              <button type="button" class="grw-modal__close" data-action="close-google-review-modal" aria-label="Close review details">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </button>
            </div>
            <div class="grw-modal__body">
              <div class="grw-review-stars" aria-label="Rated ${openedReview.rating} out of 5 stars">
                ${googleStarMarkup(openedReview.rating, 18)}
              </div>
              <p class="grw-modal__text">${escapeHtml(openedReview.text || '')}</p>
              ${openedReview.reply ? renderReplyBlock(openedReview.reply) : ''}
            </div>
          </div>
        </div>
      `
      : ''

    this.root.innerHTML = `
      <div class="grw-card">
        <div class="grw-shell">
          ${bodyHtml}
        </div>
      </div>
      ${modalHtml}
    `

    this.carouselEl = this.root.querySelector<HTMLElement>('[data-carousel]')
    this.root.removeEventListener('click', this.handleClick)
    this.root.removeEventListener('scroll', this.handleScroll, true)
    window.removeEventListener('keydown', this.handleKeyDown)
    this.root.addEventListener('click', this.handleClick)
    this.root.addEventListener('scroll', this.handleScroll, true)
    window.addEventListener('keydown', this.handleKeyDown)

    queueMicrotask(() => {
      this.syncCarouselState()
    })
  }

  async refresh() {
    await this.load()
  }

  unmount() {
    if (this.destroyed) return
    this.destroyed = true
    this.root.removeEventListener('click', this.handleClick)
    this.root.removeEventListener('scroll', this.handleScroll, true)
    window.removeEventListener('keydown', this.handleKeyDown)
    this.root.innerHTML = ''
  }
}

export function mountGoogleReviewWidget(
  target: string | Element | ArrayLike<Element>,
  options: GoogleReviewWidgetOptions
): GoogleReviewWidgetHandle[] {
  if (typeof document === 'undefined') {
    throw new Error('mountGoogleReviewWidget can only run in a browser environment')
  }

  const elements: Element[] =
    typeof target === 'string'
      ? Array.from(document.querySelectorAll(target))
      : 'length' in target
        ? Array.from(target)
        : [target]

  if (elements.length === 0) {
    throw new Error(`No widget containers found for target: ${String(target)}`)
  }

  return elements.map((element) => {
    if (!(element instanceof HTMLElement)) {
      throw new Error('Google review widget target must be an HTMLElement')
    }
    return new GoogleReviewWidgetInstance(options, element)
  })
}

export function installGoogleReviewWidgetGlobalApi() {
  if (typeof window === 'undefined') return

  window.GoogleReviewWidget = {
    mount: mountGoogleReviewWidget
  }
  window.mountGoogleReviewWidget = mountGoogleReviewWidget
}

